import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { useUser } from './context/UserContext';
import BlockedScreen from './components/BlockedScreen';

/**
 * Caterer App - Route Protection
 * Requires valid Supabase authentication.
 * If caterer account is blocked by admin, shows BlockedScreen instead of children.
 */
export default function CatererProtectedRoute({ children }) {
  const location = useLocation();
  const { catererBlocked, currentCatererId, isUserLoading } = useUser();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setIsAuthenticated(!!user);
      } catch (err) {
        console.error('Auth check error:', err);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session?.user);
    });

    return () => subscription?.unsubscribe();
  }, []);

  if (isLoading || isUserLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (catererBlocked) {
    return <BlockedScreen />;
  }

  // Logged-in users without a caterer profile must complete onboarding first.
  if (!currentCatererId) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  return children;
}
